import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import _ from 'lodash';
import { useSpring, animated } from 'react-spring';
import { Transition } from 'react-spring/renderprops.cjs';

import { buttonsV2 as Buttons } from '@breezeway/brzw-components';
import { SingleTask } from '@common/brzw_calendar/commons';
import * as calendarHelpers from '@common/brzw_calendar/helpers';
import {
  TASK_DROPDOWN_LIST_CLASS_NAME as CLASSNAME,
  TASK_DROPDOWN_LIST_HORIZONTAL_POSITIONS,
} from './constants';
import { tooltipVerticalPosition } from './helpers';

import styles from './task_dropdown_list.scss';

const sharedVariables = {
  TASK_DROPDOWN_LIST_OPTION_HEIGHT: _.parseInt(styles.TASK_DROPDOWN_LIST_OPTION_HEIGHT),
  TASK_DROPDOWN_LIST_WIDTH: _.parseInt(styles.TASK_DROPDOWN_LIST_WIDTH),
};

const TaskDropdownListContent = ({
  closeDropdown,
  containerHeight,
  containerWidth,
  displayTooltipInPortal,
  headerTitle,
  innerRef,
  isOpen,
  isTaskInactiveByFilter,
  paddingHorizontalRemoval,
  paddingVerticalRemoval,
  tasks,
}) => {
  const position = _.invoke(innerRef, 'current.getBoundingClientRect');
  const positionRight = _.get(position, 'right');
  const positionWidth = _.get(position, 'width', 0);
  const tasksSize = _.size(tasks);

  const tasksHeight = useMemo(() => tasksSize * sharedVariables.TASK_DROPDOWN_LIST_OPTION_HEIGHT, [
    tasksSize,
  ]);

  const isEnoughWidthSpace =
    containerWidth - (positionRight - positionWidth + paddingHorizontalRemoval) <=
    sharedVariables.TASK_DROPDOWN_LIST_WIDTH;

  const positionHorizontal = isEnoughWidthSpace
    ? TASK_DROPDOWN_LIST_HORIZONTAL_POSITIONS.RIGHT
    : TASK_DROPDOWN_LIST_HORIZONTAL_POSITIONS.LEFT;

  const positionVertical = useMemo(
    () => tooltipVerticalPosition(containerHeight, tasksHeight, position, paddingVerticalRemoval),
    [containerHeight, tasksHeight, position, paddingVerticalRemoval],
  );

  const animateTooltip = useSpring({ opacity: isOpen ? 1 : 0 });

  const renderTasks = _.map(tasks, (task, index) => {
    const singleTaskProps = _.omit(calendarHelpers.getSingleTaskProps(task), 'onTaskClick');
    const isInactive =
      _.isFunction(isTaskInactiveByFilter) &&
      isTaskInactiveByFilter(singleTaskProps);

    return (
      <div className={`${CLASSNAME}__task`} key={`task-dropdown-list-task-${index}-${task.id}`}>
        <SingleTask
          containerClassName={classnames({ 'brzw-calendar-single-task--inactive': isInactive })}
          displayTooltipInPortal={displayTooltipInPortal}
          tooltipSettings={{
            containerHeight: window.innerHeight,
            containerWidth: window.innerWidth,
            paddingHorizontalRemoval: 8,
            paddingVerticalRemoval,
          }}
          withText
          withToolTip
          {...singleTaskProps}
        />
      </div>
    );
  });

  return (
    <Transition
      config={{ duration: 100 }}
      duration={100}
      enter={{ opacity: 1 }}
      from={{ opacity: 0 }}
      items={isOpen}
      leave={{ opacity: 0 }}
      native
    >
      {show =>
        show &&
        (props => (
          <>
            <animated.div
              style={{ ...props, ...animateTooltip }}
              className={classnames(`${CLASSNAME}__tooltip`, {
                [`-position-${positionHorizontal}`]: positionHorizontal,
                [`-position-${positionVertical}`]: positionVertical,
              })}
            >
              <div className={classnames(`${CLASSNAME}__header`, { '-with-title': headerTitle })}>
                {headerTitle && <div className={`${CLASSNAME}__header-title`}>{headerTitle}</div>}
                <Buttons.IconButton
                  className={`${CLASSNAME}__close-btn`}
                  iconName="close"
                  onClick={closeDropdown}
                  size={Buttons.constants.BUTTON_SIZES.ICON_SMALL}
                  theme={Buttons.constants.BUTTON_THEMES.ICON_SUBTLE}
                />
              </div>
              <div className={`${CLASSNAME}__container`}>{renderTasks}</div>
            </animated.div>
          </>
        ))
      }
    </Transition>
  );
};

TaskDropdownListContent.propTypes = {
  closeDropdown: PropTypes.func,
  containerHeight: PropTypes.number,
  containerWidth: PropTypes.number,
  displayTooltipInPortal: PropTypes.bool,
  headerTitle: PropTypes.string,
  innerRef: PropTypes.shape({}),
  isOpen: PropTypes.bool,
  isTaskInactiveByFilter: PropTypes.func,
  paddingHorizontalRemoval: PropTypes.number,
  paddingVerticalRemoval: PropTypes.number,
  taskComponent: PropTypes.shape({}),
  tasks: PropTypes.arrayOf(PropTypes.shape({})),
};

TaskDropdownListContent.defaultProps = {
  innerRef: {},
  isOpen: false,
  paddingHorizontalRemoval: 0,
  paddingVerticalRemoval: 0,
  taskComponent: {},
};

export default TaskDropdownListContent;
